import React, { useEffect, useMemo } from 'react';
import { getPolyglot } from '../../../../i18n';
import {
  Box,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import {
  ReportSchedule,
  ReportType,
} from '../../../../redux/reports/api/Report.model';
import moment from 'moment';
import { getMyWorkspaceIdSelector } from '../../../../redux/stoerkID/selectors/StoerkId.selectors';
import { useAppSelector } from '../../../../redux/store.model';
import _ from 'lodash';
import { useGroupById } from '../../../../redux/groups/hooks/useGroupById';
import ChannelsMapTable from '../../ReportsTable/CreateReport/ChannelsMapTable';
import * as Yup from 'yup';
import HelperText from '../../../../theme/components/Forms/HelperText';
import LoadingButton from '@mui/lab/LoadingButton';
import { InfoModalProps } from '../../../../theme/components';
import { GroupCreateAPI } from '../../../../redux/groups/api/group.model';
import ReportAPI from '../../../../redux/reports/api/ReportAPI';
import { getInitialValuesReportSpec } from '../../ReportsTable/CreateReport/CreateReport';
import usePreferredTemperatureUnit from '../../../../redux/settings/hooks/usePreferredTemperatureUnit';

const getInitialValues = (
  workspaceId: string,
  firstReportType: ReportType | undefined,
  groupId: string | undefined,
  group: GroupCreateAPI | undefined,
  preferredTemperatureUnit: string | undefined
): Omit<ReportSchedule, 'id'> => {
  const report = getInitialValuesReportSpec(
    firstReportType,
    group,
    preferredTemperatureUnit
  );
  const { timeRange, ...reportSpec } = report;
  return {
    reportEntityMap: [{ workspaceId, groupId }],
    intervalType: 'month',
    intervalValue: 1,
    startTime: moment().add(1, 'days').toISOString(),
    reportSpec,
  };
};

const UNIT_TIMES = [
  {
    value: 'day',
    label: 'general.days',
  },
  {
    value: 'week',
    label: 'general.weeks',
  },
  {
    value: 'month',
    label: 'general.months',
  },
  {
    value: 'year',
    label: 'general.years',
  },
];

const validationSchema = Yup.object().shape({
  reportSpec: Yup.object().shape({
    reportTitle: Yup.string().required('reports.report_title_required'),
    channels: Yup.array().min(1, 'reports.channel_map_required'),
  }),
  intervalType: Yup.string().required(
    'reports.schedule.interval_unit_required'
  ),
  intervalValue: Yup.number().required(
    'reports.schedule.interval_value_required'
  ),
  startTime: Yup.string().required('reports.schedule.start_time_required'),
});
export interface CreateReportScheduleProps extends InfoModalProps {
  groupId?: string;
  reportScheduleToEdit?: ReportSchedule;
}
function CreateReportSchedule(props: CreateReportScheduleProps) {
  const { groupId, reportScheduleToEdit } = props;
  const polyglot = getPolyglot();
  const workspaceId = useAppSelector(getMyWorkspaceIdSelector) ?? '';
  const { data: reportTypes } =
    ReportAPI.endpoints.getReportTypes.useQuery(workspaceId);
  const firstReportType = _.first(reportTypes);
  const { data: group, isLoading: isLoadingGroup } = useGroupById(groupId);
  const { data: preferredTemperatureUnit } = usePreferredTemperatureUnit();
  const initialValues = useMemo(
    () =>
      getInitialValues(
        workspaceId,
        firstReportType,
        groupId,
        group,
        preferredTemperatureUnit
      ),
    [
      workspaceId,
      firstReportType,
      groupId,
      group?.composableDevice?.channels,
      preferredTemperatureUnit,
    ]
  );
  const [wantToUseIntervalForSample, setWantToUseIntervalForSample] =
    React.useState(false);

  const onChangeIntervalSample = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: boolean
  ) => {
    if (!value) {
      formik.setFieldValue('intervalSample', undefined);
    } else {
      formik.setFieldValue('intervalSample', 3600);
    }
    setWantToUseIntervalForSample(value);
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      // Handle form submission
      // eslint-disable-next-line no-console
      console.log(values);
      alert('ACTION NOT SUPPORTED!' + JSON.stringify(values));
    },
  });
  useEffect(() => {
    formik.resetForm();
  }, [props.open]);

  const reportTypeSelected = reportTypes?.find(
    (type) => type.id === formik.values.reportSpec.reportTypeId
  );

  return (
    <Container maxWidth="md" disableGutters sx={{ py: 2 }}>
      <Typography variant="h2" mb={2}>
        {polyglot.t('reports.schedule.create')}
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2} mb={2}>
          {/* <TextField
          label={polyglot.t('reports.entity_map_group_created_on')}
          value={getIn(formik.values, 'reportEntityMap.0.groupId')}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          name="reportEntityMap.0.groupId"
          disabled={true}
          select
        >
          <MenuItem value={groupId}>
            {isLoadingGroup ? (
              <Skeleton variant="text" width={60} />
            ) : (
              group?.name || 'No Group'
            )}
          </MenuItem>
        </TextField> */}
          <TextField
            label={polyglot.t('reports.type.title')}
            value={formik.values.reportSpec.reportTypeId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="reportSpec.reportTypeId"
            required
            error={Boolean(
              formik.touched.reportSpec?.reportTypeId &&
                formik.errors.reportSpec?.reportTypeId
            )}
            helperText={
              formik.touched.reportSpec?.reportTypeId &&
              formik.errors.reportSpec?.reportTypeId
                ? polyglot.t(formik.errors.reportSpec?.reportTypeId)
                : undefined
            }
            select
          >
            {reportTypes?.map((reportType) => (
              <MenuItem key={reportType.id} value={reportType.id}>
                {reportType.name}
              </MenuItem>
            )) ?? <MenuItem value={''}></MenuItem>}
          </TextField>
          <TextField
            label={polyglot.t('reports.report_title')}
            value={formik.values.reportSpec.reportTitle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="reportSpec.reportTitle"
            required
            error={Boolean(
              formik.touched.reportSpec?.reportTitle &&
                formik.errors.reportSpec?.reportTitle
            )}
            helperText={
              formik.touched.reportSpec?.reportTitle &&
              formik.errors.reportSpec?.reportTitle
                ? polyglot.t(formik.errors.reportSpec?.reportTitle)
                : polyglot.t('reports.report_helper_text')
            }
          />
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  onChange={onChangeIntervalSample}
                  value={wantToUseIntervalForSample}
                />
              }
              label={polyglot.t('reports.define_interval_sample')}
            />
            <TextField
              label={polyglot.t('reports.interval_sample')}
              value={formik.values.reportSpec.intervalTimeInSeconds}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="reportSpec.intervalTimeInSeconds"
              disabled={!wantToUseIntervalForSample}
              type="number"
              helperText={polyglot.t('reports.interval_sample_helper_text')}
            />
          </FormGroup>

          <TextField
            label={polyglot.t('reports.type.title')}
            value={formik.values.reportSpec.anatomyComponentsEnabled}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="reportSpec.anatomyComponentsEnabled"
            required
            error={Boolean(
              formik.touched.reportSpec?.anatomyComponentsEnabled &&
                formik.errors.reportSpec?.anatomyComponentsEnabled
            )}
            helperText={
              formik.touched.reportSpec?.anatomyComponentsEnabled &&
              formik.errors.reportSpec?.anatomyComponentsEnabled
                ? polyglot.t(formik.errors.reportSpec?.anatomyComponentsEnabled)
                : undefined
            }
            select
            SelectProps={{
              multiple: true,
            }}
          >
            {reportTypeSelected?.anatomyComponents?.map((component) => (
              <MenuItem key={component} value={component}>
                {polyglot.t(`reports.anatomyComponents.labels.${component}`)}
              </MenuItem>
            )) ?? <MenuItem value={''}></MenuItem>}
          </TextField>

          <Box>
            <ChannelsMapTable
              formik={formik}
              groupId={groupId}
              variant="schedule"
            />
            {formik.touched.reportSpec?.channels &&
              formik.errors.reportSpec?.channels && (
                <HelperText color="error">
                  {polyglot.t(formik.errors.reportSpec?.channels + '')}
                </HelperText>
              )}
          </Box>
          <Divider sx={{ my: 2 }} />
          {/* Interval settings */}
          <Typography variant="h6" mt={2}>
            {polyglot.t('reports.schedule.interval_execution')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label={polyglot.t('reports.schedule.interval_value')}
              value={formik.values.intervalValue}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="intervalValue"
              type="number"
              required
              error={Boolean(
                formik.touched.intervalValue && formik.errors.intervalValue
              )}
              helperText={
                formik.touched.intervalValue && formik.errors.intervalValue
                  ? polyglot.t(formik.errors.intervalValue)
                  : undefined
              }
              sx={{ width: 120 }}
            />
            <TextField
              label={polyglot.t('reports.schedule.interval_unit')}
              value={formik.values.intervalType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="intervalType"
              fullWidth
              sx={{ flex: 1 }}
              required
              error={Boolean(
                formik.touched.intervalType && formik.errors.intervalType
              )}
              helperText={
                formik.touched.intervalType && formik.errors.intervalType
                  ? polyglot.t(formik.errors.intervalType)
                  : undefined
              }
              select
            >
              {UNIT_TIMES.map((unit) => (
                <MenuItem key={unit.value} value={unit.value}>
                  {polyglot.t(unit.label)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label={polyglot.t('reports.schedule.start_time')}
            value={formik.values.startTime}
            onChange={formik.handleChange}
            fullWidth
            onBlur={formik.handleBlur}
            name="startTime"
            type="datetime-local"
            required
            error={Boolean(formik.touched.startTime && formik.errors.startTime)}
            helperText={
              formik.touched.startTime && formik.errors.startTime
                ? polyglot.t(formik.errors.startTime)
                : undefined
            }
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <LoadingButton variant="contained" color="primary" type="submit">
              {polyglot.t('button.save')}
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
    </Container>
  );
}

export default CreateReportSchedule;
